import * as React from 'react';

import {
  CurrencyCode,
  enumFinancialAccountType,
  enumPaymentMethod,
  enumPaymentTerm,
  FinancialAccountType,
  PaymentMethod,
  PaymentTerm,
} from '@rematter/frontend-lib-api-client';
import { useNextCheckNumber } from '@rematter/modules/trade/_components/scale-ticket-pro/hooks/useNextCheckNumber';
import { Button, Col, NumberField, Row, TextField } from '@rematter/ui-web';
import { LucideBanknote, LucideFileText, LucideTrash2 } from 'lucide-react';

import { useTranslations } from '../../../i18n/useTranslations';

import {
  SelectFinancialAccount,
  useFinancialAccountsQuery,
} from '~/shared/components/select-fields/SelectFinancialAccount';

export type PaymentInstallmentData = {
  id: string;
  paymentMethod: PaymentMethod;
  paymentTerm: PaymentTerm;
  amount: number;
  percentage: number;
  financialAccountId?: string;
  checkNumber?: string;
  checkMemo?: string;
  legalHold: boolean;
};

type PaymentInstallmentProps = {
  index: number;
  data: PaymentInstallmentData;
  totalAmount: number;
  currencyCode: CurrencyCode;
  facilityId: string;
  canDelete: boolean;
  checkNumberOffset: number;
  onChange: (updated: PaymentInstallmentData) => void;
  onDelete: () => void;
};

const IMMEDIATE_TERMS: PaymentTerm[] = [
  enumPaymentTerm.CAD,
  enumPaymentTerm.CIA,
  enumPaymentTerm.COD,
  enumPaymentTerm.PIA,
  enumPaymentTerm.DueUponReceipt,
  enumPaymentTerm.LOC,
];

export function isImmediatePaymentTerm(term: PaymentTerm): boolean {
  return IMMEDIATE_TERMS.includes(term);
}

function getFinancialAccountTypes(
  paymentMethod: PaymentMethod,
): FinancialAccountType[] {
  return paymentMethod === enumPaymentMethod.Cash
    ? [enumFinancialAccountType.Cash]
    : [enumFinancialAccountType.Savings, enumFinancialAccountType.Checking];
}

//
// ⚛️ ⚛️ ⚛️ PAYMENT INSTALLMENT ⚛️ ⚛️ ⚛️
//
export const PaymentInstallment = ({
  index,
  data,
  totalAmount,
  facilityId,
  canDelete,
  checkNumberOffset,
  onChange,
  onDelete,
}: PaymentInstallmentProps) => {
  const t = useTranslations();

  // Refs
  const dataRef = React.useRef(data);
  dataRef.current = data;
  const needsAutoSelectRef = React.useRef(false);
  const prevFacilityIdRef = React.useRef(facilityId);
  const prevNextCheckNumberRef = React.useRef<string | undefined>(undefined);

  const isImmediate = isImmediatePaymentTerm(data.paymentTerm);
  const isCheck = data.paymentMethod === enumPaymentMethod.Check;
  const shouldFetchCheckNumber =
    isCheck && isImmediate && !!data.financialAccountId;
  const financialAccountTypes = getFinancialAccountTypes(data.paymentMethod);

  // Data hooks
  const { data: availableAccounts, isFetching } = useFinancialAccountsQuery({
    facilityIDs: [facilityId],
    visibleInPayments: true,
    types: financialAccountTypes,
  });
  const { data: nextCheckNumber } = useNextCheckNumber({
    financialAccountId: data.financialAccountId ?? '',
    queryOptions: {
      enabled: shouldFetchCheckNumber,
    },
  });

  const hasNoAccounts = isImmediate && availableAccounts?.length === 0;

  // Callbacks
  const handlePaymentMethodChange = React.useCallback(
    (method: PaymentMethod) => {
      needsAutoSelectRef.current = true;
      onChange({
        ...data,
        paymentMethod: method,
        financialAccountId: undefined,
        checkNumber: undefined,
        checkMemo: undefined,
      });
    },
    [data, onChange],
  );

  const handlePaymentTermChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const term = e.target.value as PaymentTerm;
      const becomingImmediate =
        isImmediatePaymentTerm(term) &&
        !isImmediatePaymentTerm(data.paymentTerm);
      if (becomingImmediate) {
        needsAutoSelectRef.current = true;
      }
      onChange({
        ...data,
        paymentTerm: term,
        ...(!isImmediatePaymentTerm(term) && {
          financialAccountId: undefined,
          checkNumber: undefined,
          checkMemo: undefined,
        }),
      });
    },
    [data, onChange],
  );

  const handleAmountChange = React.useCallback(
    (amount: number) => {
      const percentage =
        totalAmount > 0
          ? Math.round((amount / totalAmount) * 100 * 100) / 100
          : 0;
      onChange({ ...data, amount, percentage });
    },
    [data, onChange, totalAmount],
  );

  const handlePercentageChange = React.useCallback(
    (percentage: number) => {
      const amount = Math.round(((totalAmount * percentage) / 100) * 100) / 100;
      onChange({ ...data, amount, percentage });
    },
    [data, onChange, totalAmount],
  );

  const handleFinancialAccountChange = React.useCallback(
    (value: string | undefined) => {
      onChange({ ...data, financialAccountId: value });
    },
    [data, onChange],
  );

  const handleCheckNumberChange = React.useCallback(
    (value: string) => {
      onChange({ ...data, checkNumber: value });
    },
    [data, onChange],
  );

  const handleCheckMemoChange = React.useCallback(
    (value: string) => {
      onChange({ ...data, checkMemo: value });
    },
    [data, onChange],
  );

  const handleLegalHoldChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...data, legalHold: e.target.checked });
    },
    [data, onChange],
  );

  // Effects
  /** Track facility changes for auto-select */
  React.useEffect(() => {
    if (prevFacilityIdRef.current !== facilityId) {
      prevFacilityIdRef.current = facilityId;
      needsAutoSelectRef.current = true;
    }
  }, [facilityId]);

  /** Auto-select first available account after facility change */
  React.useEffect(() => {
    if (!needsAutoSelectRef.current) return;
    if (isFetching) return;
    if (!availableAccounts || availableAccounts.length === 0) return;
    needsAutoSelectRef.current = false;
    const current = dataRef.current;
    onChange({ ...current, financialAccountId: availableAccounts[0].id });
  }, [availableAccounts, isFetching, onChange]);

  /** Reset previous check number ref on account change */
  React.useEffect(() => {
    prevNextCheckNumberRef.current = undefined;
  }, [data.financialAccountId]);

  /** Auto-fill check number from next check number query */
  React.useEffect(() => {
    if (
      !shouldFetchCheckNumber ||
      !nextCheckNumber ||
      nextCheckNumber === prevNextCheckNumberRef.current
    )
      return;
    prevNextCheckNumberRef.current = nextCheckNumber;
    const current = dataRef.current;
    const baseNumber = Number(nextCheckNumber);
    const adjustedNumber = isNaN(baseNumber)
      ? nextCheckNumber
      : String(baseNumber + checkNumberOffset);
    onChange({ ...current, checkNumber: adjustedNumber });
  }, [nextCheckNumber, shouldFetchCheckNumber, checkNumberOffset, onChange]);

  return (
    <Col
      gap3
      className="@PaymentInstallment rounded-lg border border-neutral/10 p-4"
    >
      <Row xBetween yCenter>
        <span className="font-semibold">
          {t.scaleTicketPro.finishModal.paymentLabel} #{index + 1}
        </span>
        <Row gap4 yCenter>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={data.legalHold}
              onChange={handleLegalHoldChange}
              className="size-4 rounded border-neutral/30"
            />
            {t.scaleTicketPro.finishModal.legalHold}
          </label>
          {canDelete && (
            <Button
              variant="ghost"
              color="failure"
              onPress={onDelete}
              className="h-8"
            >
              <Row gap1>
                <LucideTrash2 size={14} />
                {t.scaleTicketPro.finishModal.removePayment}
              </Row>
            </Button>
          )}
        </Row>
      </Row>

      <div className="grid grid-cols-3 gap-3">
        <Col gap2>
          <div className="text-sm font-medium">
            {t.scaleTicketPro.finishModal.paymentMethod}
          </div>
          <Row gap2>
            <Button
              variant={
                data.paymentMethod === enumPaymentMethod.Cash
                  ? 'solid'
                  : 'outline'
              }
              color={
                data.paymentMethod === enumPaymentMethod.Cash
                  ? 'primary'
                  : 'neutral'
              }
              className="flex-1"
              onPress={() => handlePaymentMethodChange(enumPaymentMethod.Cash)}
            >
              <Row gap1>
                <LucideBanknote size={16} />
                {t.scaleTicketPro.finishModal.cash}
              </Row>
            </Button>
            <Button
              variant={
                data.paymentMethod === enumPaymentMethod.Check
                  ? 'solid'
                  : 'outline'
              }
              color={
                data.paymentMethod === enumPaymentMethod.Check
                  ? 'primary'
                  : 'neutral'
              }
              className="flex-1"
              onPress={() => handlePaymentMethodChange(enumPaymentMethod.Check)}
            >
              <Row gap1>
                <LucideFileText size={16} />
                {t.scaleTicketPro.finishModal.check}
              </Row>
            </Button>
          </Row>
        </Col>

        <Col gap2>
          <div className="text-sm font-medium">
            {t.scaleTicketPro.finishModal.paymentTerm}
          </div>
          <select
            value={data.paymentTerm}
            onChange={handlePaymentTermChange}
            className="bg-background h-10 rounded-md border border-neutral/20 px-3 text-sm"
          >
            {IMMEDIATE_TERMS.map((term) => (
              <option key={term} value={term}>
                {t.scaleTicketPro.finishModal.paymentTermLabels[term]}
              </option>
            ))}
          </select>
        </Col>

        <Row gap2>
          <NumberField
            label={t.scaleTicketPro.finishModal.amount}
            value={data.amount}
            onChange={handleAmountChange}
            formatOptions={{
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }}
            showSteppers={false}
            className="flex-1"
          />
          <NumberField
            label={t.scaleTicketPro.finishModal.percentage}
            value={data.percentage}
            onChange={handlePercentageChange}
            formatOptions={{
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            }}
            showSteppers={false}
            className="w-24"
          />
        </Row>
      </div>

      {isImmediate && (
        <div className="grid grid-cols-3 gap-3">
          <Col gap1>
            <SelectFinancialAccount
              label={t.scaleTicketPro.finishModal.financialAccount}
              value={data.financialAccountId}
              onChange={handleFinancialAccountChange}
              facilityIDs={[facilityId]}
              visibleInPayments
              types={financialAccountTypes}
              isRequired
            />
            {hasNoAccounts && (
              <span className="text-xs text-failure">
                {t.scaleTicketPro.finishModal.noFinancialAccounts}
              </span>
            )}
          </Col>
          {isCheck && (
            <>
              <TextField
                label={t.scaleTicketPro.finishModal.checkNumber}
                value={data.checkNumber ?? ''}
                onChange={handleCheckNumberChange}
                isRequired
              />
              <TextField
                label={t.scaleTicketPro.finishModal.checkMemo}
                value={data.checkMemo ?? ''}
                onChange={handleCheckMemoChange}
              />
            </>
          )}
        </div>
      )}
    </Col>
  );
};
